import React from 'react';
import { Helmet } from 'react-helmet-async';
import { MapPin, Phone, Calendar, ArrowRight, CheckCircle, Clock, Monitor, Heart, Users, Shield, Globe } from 'lucide-react';
import CTA from '../../components/CTA';

const BushmillsHypnotherapy: React.FC = () => {
  return (
    <main>
      <Helmet>
        <title>Hypnotherapy Bushmills | Distillery Worker & Heritage Tourism Support</title>
        <meta
          name="description"
          content="Professional hypnotherapy for Bushmills village. Support for distillery workers, tourism service burnout, village privacy concerns, and heritage tourism stress. CNHC registered. Belfast clinic and online sessions available."
        />
        <meta name="keywords" content="hypnotherapy Bushmills, hypnotherapist Bushmills, distillery worker stress, heritage tourism burnout, small village privacy, Giant's Causeway stress, tourism anxiety North Coast, therapist Bushmills" />
        <link rel="canonical" href="https://www.sharonclare-hypnotherapy.com/bushmills-hypnotherapy/" />
        <meta property="og:title" content="Hypnotherapy Bushmills | Distillery Worker & Heritage Tourism Support" />
        <meta property="og:description" content="Professional hypnotherapy for Bushmills. Support for distillery workers, tourism burnout, village privacy. Belfast clinic and online sessions available." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Sharon Clare Hypnotherapy - Serving Bushmills",
            "description": "CNHC registered hypnotherapy serving Bushmills village and surrounding area. Specializing in distillery worker stress (shift work, tourism-facing roles, burnout), heritage tourism service stress (Giant's Causeway visitor management), village-scale privacy concerns, single-industry economic anxiety, and seasonal tourism pressures. In-person sessions at our Belfast clinic or online sessions for confidentiality in small village context.",
            "url": "https://www.sharonclare-hypnotherapy.com/bushmills-hypnotherapy/",
            "telephone": "07932139245",
            "email": "Sharon@sharonclare-hypnotherapy.com",
            "image": "https://www.sharonclare-hypnotherapy.com/images/sharon-clare.jpg",
            "areaServed": {
              "@type": "City",
              "name": "Bushmills, County Antrim",
              "containedInPlace": {
                "@type": "Country",
                "name": "Northern Ireland"
              }
            },
            "location": [
              {
                "@type": "Place",
                "name": "The Arzen Collective",
                "address": {
                  "@type": "PostalAddress",
                  "streetAddress": "The Arzen Collective, 13B Antrim Street",
                  "addressLocality": "Lisburn",
                  "addressRegion": "County Antrim",
                  "postalCode": "BT28",
                  "addressCountry": "GB"
                }
              },
              {
                "@type": "Place",
                "name": "Belfast Clinic",
                "address": {
                  "@type": "PostalAddress",
                  "streetAddress": "Belfast Wellness Clinic, 228 Ormeau Road",
                  "addressLocality": "Belfast",
                  "addressRegion": "County Down",
                  "postalCode": "BT7 2FZ",
                  "addressCountry": "GB"
                }
              }
            ],
            "sameAs": [
              "https://instagram.com/sharonclarehypnotherapy",
              "https://www.facebook.com/SharonClareHypnotherapy/",
              "https://www.linkedin.com/in/sharonclare-hypnotherapy"
            ],
            "medicalSpecialty": "Clinical Hypnotherapy",
            "serviceType": [
              "Distillery Worker Mental Health",
              "Heritage Tourism Burnout Support",
              "Village Privacy-Conscious Support",
              "Tourism Service Stress Management",
              "Economic Anxiety Support"
            ]
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-gray-900 mb-6">
              Professional Hypnotherapy for Bushmills
            </h1>
            <p className="text-xl sm:text-2xl text-gray-600 mb-6 leading-relaxed">
              Distillery worker support, heritage tourism burnout, and confidential care for village community
            </p>
            <div className="bg-white rounded-xl p-6 mb-8 inline-block">
              <div className="flex items-center justify-center space-x-3 mb-4">
                <Monitor className="w-6 h-6 text-accent-600" />
                <p className="text-lg text-gray-700 font-semibold">
                  Online sessions from home, or in person at the Belfast and Lisburn clinics
                </p>
              </div>
              <p className="text-gray-600">
                Secure video sessions offer complete confidentiality from home — no travel, and nothing visible to the village. In-person appointments are available at Belfast Wellness Clinic on the Ormeau Road and at The Arzen Collective in Lisburn.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/book"
                className="btn-primary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Book Free Consultation
              </a>
              <a
                href="tel:07932139245"
                className="btn-secondary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call 07932 139245
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6">
              Hypnotherapy for Bushmills Heritage Tourism Village
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                Bushmills is one of Northern Ireland's most internationally recognized villages - home to the world's oldest licensed whiskey distillery and just three miles from the Giant's Causeway UNESCO World Heritage Site. With a population of only 1,300 people, this tiny heritage tourism village experiences unique mental health pressures: economic dependence on a single employer (the distillery), constant flow of international tourists through a very small community, shift work stress for distillery production staff, tourism-facing burnout for visitor center workers, and the particular challenge of seeking mental health support in a village where "everyone knows everyone and everyone's business." As a CNHC registered hypnotherapist with 15 years of nursing experience at Belfast City Hospital, I understand the specific stresses of living and working in a place where your village identity is defined by international tourism attractions and privacy is essentially non-existent.
              </p>
              <p>
                For Bushmills residents, <strong>online sessions via secure video call</strong> are usually the most practical option — and they offer crucial discretion in a tiny village where seeking help can feel highly visible. If you would rather meet face to face, I hold clinics at Belfast Wellness Clinic, 228 Ormeau Road, Belfast, where days and times vary and early evening appointments are available, and at The Arzen Collective, 13B Antrim Street, Lisburn. Whether you're managing shift work stress at the distillery, experiencing burnout from tourism-facing customer service roles, coping with the economic anxiety of single-industry dependence, dealing with the pressure of living in a place that's essentially a tourist attraction, or navigating seasonal employment uncertainty, confidential professional support is available without the visibility that can come with seeking help in a 1,300-person community.
              </p>
              <p>
                Bushmills' extreme small-scale dynamics are both its strength and its challenge - tight-knit community support exists alongside limited anonymity and traditional attitudes that can make mental health help-seeking feel exposed. Connecting online, or traveling to the Belfast clinic on the Ormeau Road or the Lisburn clinic on Antrim Street, provides privacy while still accessing culturally aware care from someone who understands heritage tourism village pressures. I work with individuals facing distillery worker stress (shift patterns, tourism service demands, corporate workplace changes), tourism service burnout (managing 750,000+ annual Giant's Causeway visitors flowing through your tiny village), heritage tourism pressure (living in a place constantly observed by international tourists), single-industry economic vulnerability, village-scale privacy concerns, and the unique mental health challenges of life in Northern Ireland's smallest and most internationally visible community.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              How Hypnotherapy Can Help Bushmills Community
            </h2>
            <div className="grid gap-8 sm:grid-cols-2">
              <div className="bg-white p-6 rounded-xl">
                <div className="flex items-start space-x-4">
                  <CheckCircle className="w-6 h-6 text-accent-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Distillery Worker Stress</h3>
                    <p className="text-gray-600">
                      Support for Old Bushmills Distillery staff managing shift work patterns, tourism-facing customer service pressure in visitor center, production stress, corporate ownership changes (Diageo), and the mental health impact of working for the employer that defines your entire village's economy and identity.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-xl">
                <div className="flex items-start space-x-4">
                  <CheckCircle className="w-6 h-6 text-accent-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Heritage Tourism Burnout</h3>
                    <p className="text-gray-600">
                      Help for tourism service workers managing constant flow of international visitors (Giant's Causeway's 750,000+ annual visitors), customer service pressure with global tourists, performance expectations representing "world-famous" heritage brand, and exhaustion from living in a place that functions as a tourist attraction rather than just a village.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-xl">
                <div className="flex items-start space-x-4">
                  <CheckCircle className="w-6 h-6 text-accent-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Village-Scale Privacy Support</h3>
                    <p className="text-gray-600">
                      Confidential care recognizing that 1,300 population means essentially zero anonymity - "everyone knows everyone and everyone's business" is extreme in Bushmills. In-person sessions at our Belfast clinic provide discretion away from the village, or online sessions offer complete confidentiality from home. Understanding of small village help-seeking barriers and traditional attitudes.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-xl">
                <div className="flex items-start space-x-4">
                  <CheckCircle className="w-6 h-6 text-accent-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Single-Industry Economic Anxiety</h3>
                    <p className="text-gray-600">
                      Support for managing the stress of economic dependence on one employer (distillery) and one attraction (Giant's Causeway) - when village economy depends almost entirely on heritage tourism, any downturn affects the entire community. Address anxiety about economic vulnerability and personal financial security tied to single-industry health.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-xl">
                <div className="flex items-start space-x-4">
                  <CheckCircle className="w-6 h-6 text-accent-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Living in Tourist Attraction Stress</h3>
                    <p className="text-gray-600">
                      Help managing the unique pressure of village life constantly observed by international tourists - your daily life happens in a place that hundreds of thousands of visitors photograph, tour through, and experience as heritage attraction. Address feeling of "performing" village life, infrastructure strain (traffic, parking, noise), and loss of normal community dynamics during peak season.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-xl">
                <div className="flex items-start space-x-4">
                  <CheckCircle className="w-6 h-6 text-accent-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Seasonal Tourism Employment Stress</h3>
                    <p className="text-gray-600">
                      Support for those facing intense summer season pressure (Giant's Causeway and distillery visitor center extremely busy May-September) followed by quiet winter months. Address financial anxiety about seasonal income patterns, exhaustion after peak season, and employment uncertainty for tourism-dependent workers.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-xl">
                <div className="flex items-start space-x-4">
                  <CheckCircle className="w-6 h-6 text-accent-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Shift Work Mental Health</h3>
                    <p className="text-gray-600">
                      Help for distillery production staff managing shift work patterns (distilling operates continuously), sleep disruption, social life impacts from irregular hours, and cumulative mental health effects of shift work in manufacturing environment. Address work-life balance challenges unique to shift patterns.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-xl">
                <div className="flex items-start space-x-4">
                  <CheckCircle className="w-6 h-6 text-accent-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Rural Heritage Village Isolation</h3>
                    <p className="text-gray-600">
                      Support for the paradox of living somewhere internationally famous but geographically isolated with limited amenities. Address loneliness despite tourist crowds (tourists don't provide meaningful social connection), rural isolation especially in winter when visitors disappear, and the mental health challenges of tiny-scale community living.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Sharon Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              Why Bushmills Residents Choose Sharon Clare
            </h2>
            <div className="grid gap-8 sm:grid-cols-2">
              <div className="flex items-start space-x-4">
                <Shield className="w-6 h-6 text-accent-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Complete Confidentiality Understanding</h3>
                  <p className="text-gray-600">
                    Deep understanding that seeking mental health support in a 1,300-person village feels highly visible. I provide completely confidential care with option of in-person sessions at our Belfast clinic (providing discretion away from the village) or online sessions from home (complete privacy). Everything discussed is protected under professional standards.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <Users className="w-6 h-6 text-accent-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Heritage Tourism Community Awareness</h3>
                  <p className="text-gray-600">
                    Experience working with heritage tourism communities facing unique pressures - living in internationally visible locations, managing tourist flows through tiny villages, economic dependence on heritage attractions, and the stress of village identity being externally defined by tourism rather than community itself.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <Monitor className="w-6 h-6 text-accent-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Flexible, Privacy-Conscious Options</h3>
                  <p className="text-gray-600">
                    Choose in-person sessions at our Belfast clinic for face-to-face support away from village visibility, or online sessions via secure video call for complete confidentiality. Both options accommodate shift work, distillery schedules, and tourism sector demands.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <Heart className="w-6 h-6 text-accent-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Professional Healthcare Background</h3>
                  <p className="text-gray-600">
                    15 years as a registered nurse at Belfast City Hospital before specializing in hypnotherapy. Understanding of healthcare systems, shift work impacts on mental health, rural healthcare access challenges, and the particular barriers small communities face seeking mental health support.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs Section */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              Common Questions from Bushmills Clients
            </h2>
            <div className="space-y-6">
              <div className="bg-white p-6 rounded-xl">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  Will seeking help in such a tiny village be kept confidential?
                </h3>
                <p className="text-gray-600">
                  Absolutely. I understand that Bushmills' population of 1,300 means essentially zero anonymity - this is the most privacy-sensitive community I work with. Everything discussed in sessions is completely confidential under professional standards. Many clients appreciate that in-person sessions at our Belfast clinic provide crucial discretion away from the village where "everyone knows everyone", or online sessions offer complete privacy from home. You can access professional support without the visibility that comes with seeking help in such a small, tight-knit village.
                </p>
              </div>

              <div className="bg-white p-6 rounded-xl">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  Can hypnotherapy help with distillery shift work stress and burnout?
                </h3>
                <p className="text-gray-600">
                  Yes. Distillery workers face specific mental health challenges - shift work disrupts sleep and social life, production environments create constant performance pressure, tourism-facing roles (visitor center, tours) require intense customer service with international guests, and corporate ownership changes (Diageo) can shift workplace culture. Hypnotherapy helps manage shift work impacts on mental health, addresses tourism service burnout, supports adjustment to workplace changes, and provides strategies for work-life balance when your employer is also your village's defining economic feature. Many distillery workers appreciate flexible scheduling around shift patterns.
                </p>
              </div>

              <div className="bg-white p-6 rounded-xl">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  How do you address the stress of living in a place that's essentially a tourist attraction?
                </h3>
                <p className="text-gray-600">
                  Living in heritage tourism villages creates unique pressure - your daily life happens in a place that hundreds of thousands of international visitors tour through, photograph, and experience as an attraction rather than a living community. Hypnotherapy helps process the feeling of "performing" village life, manages resentment around constant tourist presence (especially peak summer season), addresses infrastructure strain impacts (traffic, parking congestion, noise), and works on maintaining sense of normal community life despite international visibility. We acknowledge the paradox of loving where you live while finding the tourism overlay exhausting.
                </p>
              </div>

              <div className="bg-white p-6 rounded-xl">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  I'm anxious about Bushmills' economic dependence on the distillery and Giant's Causeway - can therapy help?
                </h3>
                <p className="text-gray-600">
                  Yes. Single-industry economic vulnerability creates real anxiety - when your village depends almost entirely on one employer (distillery) and proximity to one attraction (Giant's Causeway), any downturn affects the entire community (COVID lockdowns demonstrated this dramatically). Hypnotherapy doesn't change economic realities, but it helps manage personal anxiety about economic vulnerability, builds resilience for uncertainty, addresses constant worry about what happens if tourism declines or distillery changes, and works on what you can control while accepting what you cannot. We develop mental health strategies for living with economic uncertainty.
                </p>
              </div>

              <div className="bg-white p-6 rounded-xl">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  Can online sessions work for shift workers or tourism staff with irregular schedules?
                </h3>
                <p className="text-gray-600">
                  Absolutely. Online sessions via secure video call are ideal for distillery shift workers (rotating patterns make fixed appointment times difficult), tourism sector staff with seasonal schedule variations, or anyone whose work doesn't follow standard hours. We can schedule flexibly around your patterns, and you connect from home without travel time. Online sessions are fully professional, completely confidential, and just as effective as in-person for hypnotherapy work. Many Bushmills clients prefer online for both scheduling convenience and privacy in such a small village.
                </p>
              </div>

              <div className="bg-white p-6 rounded-xl">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  How can hypnotherapy help with tourism service burnout managing Giant's Causeway visitors?
                </h3>
                <p className="text-gray-600">
                  Tourism service workers in Bushmills face intense pressure - the Giant's Causeway attracts 750,000+ visitors annually, many flowing through your tiny village, creating constant customer service demands. Hypnotherapy addresses hospitality burnout, manages performance anxiety around representing "world-famous" heritage attractions to international tourists, helps with emotional exhaustion from high-volume customer interaction, and provides strategies for maintaining mental health during intense peak season (May-September extremely busy). We work on sustainable approaches to tourism service work and recovering from seasonal burnout.
                </p>
              </div>

              <div className="bg-white p-6 rounded-xl">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  I feel isolated even though thousands of tourists visit - is that normal?
                </h3>
                <p className="text-gray-600">
                  Completely normal. Tourists don't provide meaningful social connection - they're passing through experiencing your village as an attraction, not engaging with you as a person or community member. Hypnotherapy helps process the paradox of living somewhere internationally famous but feeling isolated, addresses loneliness in tiny-scale community (1,300 people limits social options), manages seasonal isolation when winter brings darkness and tourist season ends, and works on building genuine connection despite transient tourist presence. The isolation of heritage tourism villages is real and valid.
                </p>
              </div>

              <div className="bg-white p-6 rounded-xl">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  Will you understand the cultural context of such a small, traditional Northern Irish village?
                </h3>
                <p className="text-gray-600">
                  Yes. I work extensively with small Northern Irish communities and understand the cultural dynamics - traditional attitudes toward mental health, "everyone knows everyone" visibility, pride in community alongside frustration with limitations, and help-seeking barriers that exist in tight-knit villages. I respect the cultural context while providing professional mental health support. Many Bushmills clients appreciate not having to explain why seeking help in a 1,300-person village feels complicated - I understand the dynamics already.
                </p>
              </div>

              <div className="bg-white p-6 rounded-xl">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  How can therapy address seasonal employment financial stress in tourism sector?
                </h3>
                <p className="text-gray-600">
                  Seasonal employment creates real financial anxiety - intense summer months (Giant's Causeway and distillery visitor center extremely busy) followed by quiet winter with limited income. Hypnotherapy helps retrain your nervous system's response to seasonal income patterns, manages anxiety about making summer earnings last through winter, addresses pressure to maximize peak season income, and builds resilience for cyclical employment. We work on practical strategies for living with seasonal financial uncertainty and reducing the consuming worry that can accompany boom-and-bust tourism work.
                </p>
              </div>

              <div className="bg-white p-6 rounded-xl">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  What are your fees and how quickly can I be seen?
                </h3>
                <p className="text-gray-600">
                  The initial consultation runs 90 minutes and includes assessment and your first session. After that, single sessions are 60 minutes, and a discounted 4-session package is available. Most clients see meaningful improvement within 4-6 sessions for specific issues like shift work stress or tourism burnout. I typically have availability within 1-2 weeks. Book directly via the booking page for fastest scheduling, or call 07932 139245 to discuss your needs and arrange a time that works around your work schedule. I understand discretion matters in a small village.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              What North Coast Clients Say
            </h2>
            <div className="grid gap-8 sm:grid-cols-2">
              <div className="bg-gray-50 p-6 rounded-xl">
                <div className="flex items-center mb-4">
                  <div className="flex text-accent-600">
                    {'★'.repeat(5)}
                  </div>
                </div>
                <p className="text-gray-700 mb-4 italic">
                  "Working at a heritage tourism site means you're 'on' constantly - representing this famous brand to international visitors, high standards expected, exhausting. Sharon understood the burnout that comes with tourism service work in such a high-profile location. Online sessions were perfect for privacy in a tiny village where everyone knows everyone."
                </p>
                <p className="text-gray-600 font-semibold">– Tourism worker, North Coast</p>
              </div>

              <div className="bg-gray-50 p-6 rounded-xl">
                <div className="flex items-center mb-4">
                  <div className="flex text-accent-600">
                    {'★'.repeat(5)}
                  </div>
                </div>
                <p className="text-gray-700 mb-4 italic">
                  "Shift work at the distillery was destroying my sleep and mental health. Couldn't talk to colleagues about it - we all work together in a tiny village, you don't admit struggling. Sharon helped me manage the shift work impacts without feeling like I was betraying the company or admitting weakness. Actually sleeping better now."
                </p>
                <p className="text-gray-600 font-semibold">– Manufacturing worker, County Antrim</p>
              </div>

              <div className="bg-gray-50 p-6 rounded-xl">
                <div className="flex items-center mb-4">
                  <div className="flex text-accent-600">
                    {'★'.repeat(5)}
                  </div>
                </div>
                <p className="text-gray-700 mb-4 italic">
                  "Living somewhere that's basically a tourist attraction is surreal - your home is on international tours, thousands of people flow through constantly. I felt guilty complaining because 'look where you live!' but it's exhausting. Sharon validated that living in a heritage tourism village has real mental health impacts. First person who got it."
                </p>
                <p className="text-gray-600 font-semibold">– Resident, North Coast</p>
              </div>

              <div className="bg-gray-50 p-6 rounded-xl">
                <div className="flex items-center mb-4">
                  <div className="flex text-accent-600">
                    {'★'.repeat(5)}
                  </div>
                </div>
                <p className="text-gray-700 mb-4 italic">
                  "Economic anxiety is real when your entire village depends on one employer and one tourist attraction - COVID proved that. What happens if distillery changes or tourism declines? Sharon helped me manage that constant background worry about economic vulnerability without it consuming me. Can't change the reality but can change how I cope with it."
                </p>
                <p className="text-gray-600 font-semibold">– Client, County Antrim</p>
              </div>

              <div className="bg-gray-50 p-6 rounded-xl">
                <div className="flex items-center mb-4">
                  <div className="flex text-accent-600">
                    {'★'.repeat(5)}
                  </div>
                </div>
                <p className="text-gray-700 mb-4 italic">
                  "Small village meant I couldn't seek help locally - too visible, traditional attitudes, everyone would know. Being able to drive to the Belfast clinic for sessions was perfect - close enough to be practical, far enough for discretion. Sharon understands Northern Irish village dynamics without me having to explain why privacy matters so much."
                </p>
                <p className="text-gray-600 font-semibold">– Client, North Coast</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Crisis Resources */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6 text-center">
              Crisis Support Resources
            </h2>
            <div className="bg-white p-8 rounded-xl">
              <p className="text-gray-700 mb-6">
                If you're experiencing a mental health crisis and need immediate support, these services are available 24/7:
              </p>
              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Lifeline NI</h3>
                  <p className="text-gray-600 mb-2">24/7 crisis helpline for anyone in distress or despair</p>
                  <a href="tel:08088088000" className="text-accent-600 hover:text-accent-700 font-semibold">
                    0808 808 8000
                  </a>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Northern Health and Social Care Trust Crisis Line</h3>
                  <p className="text-gray-600 mb-2">Mental health crisis support for Northern Trust area (includes Bushmills)</p>
                  <a href="tel:08001833333" className="text-accent-600 hover:text-accent-700 font-semibold">
                    0800 183 3333
                  </a>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Samaritans</h3>
                  <p className="text-gray-600 mb-2">Confidential emotional support available 24/7</p>
                  <a href="tel:116123" className="text-accent-600 hover:text-accent-700 font-semibold">
                    116 123
                  </a>
                </div>
              </div>
              <p className="text-gray-600 mt-6 text-sm">
                In a life-threatening emergency, always call 999 or go to your nearest A&E.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTA />
    </main>
  );
};

export default BushmillsHypnotherapy;
